/**
 * BerichtePage (Phase 3, §6.3) — tabbed financial reports.
 *
 * Three tabs — Cashflow / Ausgaben nach Kategorie / Vermögen — with the active
 * tab kept in the `?tab=` search param (deep-linkable under the AppShell SPA
 * fallback). A shared `RangeSelect` feeds every `{from,to}`-keyed report query;
 * each tab renders the spine chart primitives (which carry their own
 * ChartFrame + "Tabelle anzeigen" fallback).
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { SegmentedControl } from '../components/ui/SegmentedControl.tsx';
import type { MonthRange } from '../lib/queryKeys.ts';
import { RangeSelect, defaultRange } from './berichte/RangeSelect.tsx';
import { CashflowReport } from './berichte/CashflowReport.tsx';
import { CategoryReport } from './berichte/CategoryReport.tsx';
import { NetWorthReport } from './berichte/NetWorthReport.tsx';

type Tab = 'cashflow' | 'kategorien' | 'vermoegen';

const TABS: { value: Tab; label: string }[] = [
  { value: 'cashflow', label: 'Cashflow' },
  { value: 'kategorien', label: 'Ausgaben nach Kategorie' },
  { value: 'vermoegen', label: 'Vermögen' },
];

function isTab(value: string | null): value is Tab {
  return value === 'cashflow' || value === 'kategorien' || value === 'vermoegen';
}

export default function BerichtePage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = isTab(raw) ? raw : 'cashflow';
  const [range, setRange] = useState<MonthRange>(defaultRange);

  const setTab = (next: string) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <div className="flex flex-col">
      <PageHeader title="Berichte" subtitle="Cashflow, Kategorien und Vermögen" />

      <div className="w-full space-y-6 px-4 py-6 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="overflow-x-auto">
            <SegmentedControl<Tab>
              value={tab}
              onChange={setTab}
              ariaLabel="Bericht"
              options={TABS}
            />
          </div>
          <RangeSelect value={range} onChange={setRange} />
        </div>

        {tab === 'cashflow' ? <CashflowReport range={range} /> : null}
        {tab === 'kategorien' ? <CategoryReport range={range} /> : null}
        {tab === 'vermoegen' ? <NetWorthReport range={range} /> : null}
      </div>
    </div>
  );
}
